import {
  Directive,
  computed,
  inject,
  effect,
  Renderer2,
  ElementRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
} from "@angular/core";
import { BrnSelectOption } from "@spartan-ng/brain/select";
import { HlmCheckbox } from "@spartan-ng/helm/checkbox";

/**
 * Directive that automatically shows checkbox for multi-select options
 *
 * Usage: Add to <hlm-option> elements in multi-select contexts
 * <hlm-option hlmMultiSelectOption [value]="item.id">Label</hlm-option>
 *
 * Features:
 * - Shows checkbox with checkmark on selected items
 * - Hides default check icon
 * - Automatically syncs with select state
 */
@Directive({
  selector: "hlm-option[hlmMultiSelectOption]",
  standalone: true,
})
export class HlmMultiSelectOption {
  private readonly _brnSelectOption = inject(BrnSelectOption);
  private readonly _renderer = inject(Renderer2);
  private readonly _el = inject(ElementRef);
  private readonly _injector = inject(EnvironmentInjector);
  private _checkboxComponentRef: ComponentRef<HlmCheckbox> | null = null;

  // Check if option is selected using BrnSelectOption's built-in signal
  private readonly _isSelected = computed(() => {
    return this._brnSelectOption.selected();
  });

  constructor() {
    // Effect to setup checkbox on mount
    effect(() => {
      const element = this._el.nativeElement as HTMLElement;

      // Add padding class
      this._renderer.addClass(element, "!pr-2");

      // Find and hide the default check icon span
      const checkSpan = element.querySelector("span:first-child");
      if (checkSpan) {
        this._renderer.setStyle(checkSpan, "display", "none");
      }

      // Create and insert checkbox if not already present
      if (!this._checkboxComponentRef) {
        this._createCheckbox(element);
      }

      // Update checkbox state
      this._updateCheckboxState();
    });

    // Effect to update checkbox checked state when selection changes
    effect(() => {
      this._isSelected(); // Track changes
      this._updateCheckboxState();
    });
  }

  private _createCheckbox(parentElement: HTMLElement): void {
    // Create the checkbox component dynamically
    const componentRef = createComponent(HlmCheckbox, {
      environmentInjector: this._injector,
    });

    // Set checkbox properties
    componentRef.setInput("checked", this._isSelected());

    // Add class to the checkbox host element
    const checkboxElement = componentRef.location.nativeElement;
    this._renderer.addClass(checkboxElement, "mr-2");
    this._renderer.addClass(checkboxElement, "pointer-events-none");

    // Attach the component to the DOM
    componentRef.hostView.detectChanges();

    // Insert at the beginning of the option
    const firstChild = parentElement.firstChild;
    if (firstChild) {
      this._renderer.insertBefore(parentElement, checkboxElement, firstChild);
    } else {
      this._renderer.appendChild(parentElement, checkboxElement);
    }

    this._checkboxComponentRef = componentRef;
  }

  private _updateCheckboxState(): void {
    if (!this._checkboxComponentRef) return;

    const isChecked = this._isSelected();
    this._checkboxComponentRef.setInput("checked", isChecked);
    this._checkboxComponentRef.hostView.detectChanges();
  }
}
